
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { CellForVSelect, ListItem, ListItemType } from '../todolist.model';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import RadioGroupEditor from './RadioGroupEditor';

@Component({
  components: {
    RadioGroupEditor,
    vSelect,
  },
})
export default class Entry extends Vue {
  @Prop({ required: true })
  readonly todo!: ListItem;
  @Prop({ required: true })
  readonly cells!: CellForVSelect[];
  @Prop({ required: true })
  readonly editable!: boolean;
  @Prop({ required: true })
  readonly editorMode!: boolean;
  @Prop({ default: true })
  readonly deletable!: boolean;

  editTodoText: string | null = null;
  editTodoTypedData: any | null = null;
  isEditingTypeData: boolean = false;

  //needed by vue html
  ListItemType = ListItemType;

  get canCheck(): boolean {
    return this.editable
        && (this.todo.type !== ListItemType.ScanCell || this.editorMode); // when type == ScanCell only allow when in editorMode
  }

  get canEditScanCell(): boolean {
    return this.editable && this.editorMode;
  }

  get canRequired(): boolean {
    switch (this.todo.type) {
      case ListItemType.Default:
      case ListItemType.Comment:
      case ListItemType.ScanCell:
      case ListItemType.Signature:
      case ListItemType.RadioGroup:
        return true;
      default:
        return false;
    }
  }

  get editTodoTypedDataForIntegerVSelect(): number | null {
    return this.editTodoTypedData;
  }

  set editTodoTypedDataForIntegerVSelect(value: number | null) {
    this.editTodoTypedData = value;
    this.saveTodoTypedData();
  }

  get radioGroupEntries(): { label: string, checked: boolean }[] {
    return this.parseRadioGroupEntries();
  }

  get required(): boolean {
    return this.todo.required ?? false;
  }

  set required(value: boolean) {
    this.todo.required = value;
    this.notifyChange();
  }

  //functions
  parseRadioGroupEntries(): { label: string, checked: boolean }[] {
    if (this.todo.type !== ListItemType.RadioGroup) {
      return [];
    }
    if (Array.isArray(this.todo.typedData)) {
      return this.todo.typedData;
    } else if (typeof this.todo.typedData === 'string') {
      let parsed: { label: string, checked: boolean }[] = [];
      try {
        parsed = JSON.parse(this.todo.typedData);
      } catch (_) {
      }
      if (!Array.isArray(parsed)) {
        parsed = [];
      }
      return parsed;
    } else {
      return [];
    }
  }

  //functions
  saveTodoText(): void {
    if (!this.editable) {
      return;
    }
    if (!this.editTodoText?.trim().length) {
      return;
    }
    this.todo.text = this.editTodoText!;
    this.todo.completed = false;
    this.editTodoText = null;
    this.notifyChange();
  }

  saveTodoTypedData(): void {
    if (!this.editable) {
      return;
    }
    this.todo.typedData = this.editTodoTypedData!;
    if (this.todo.type === ListItemType.RadioGroup) {
      this.todo.completed = this.parseRadioGroupEntries().some(e => e.checked);
    }
    this.editTodoTypedData = null;
    this.isEditingTypeData = false;
    this.notifyChange();
  }

  startEditTodo(): void {
    if (!this.editable) {
      return;
    }
    this.editTodoText = this.todo.text;
    this.$nextTick(() => (this.$refs.editBox as HTMLInputElement).focus());
  }

  startEditTypedData(): void {
    if (!this.editable) {
      return;
    }
    this.editTodoTypedData = this.todo.typedData ?? null;
    this.isEditingTypeData = true;
    this.$nextTick(() => {
      //clear radio group selection, this is currently the only way to clear the selection
      (this.$refs.radioGroupEditor as RadioGroupEditor).clearSelection();

      //focus input element
      if ((this.$refs.typedEditBox as HTMLInputElement)?.focus) {
        (this.$refs.typedEditBox as HTMLInputElement)?.focus();
      }
    });
  }

  selectRadioGroupEntry(index: number, checked: boolean): void {
    this.radioGroupEntries.forEach(e => e.checked = false);
    this.radioGroupEntries[index].checked = checked;
    this.todo.typedData = JSON.stringify(this.radioGroupEntries); //TODO remove stringify when app can deal with any-typed data
    this.todo.completed = this.radioGroupEntries.some(e => e.checked);
    this.notifyChange();
  }

  removeTodo(): void {
    if (!this.editable) {
      return;
    }
    this.$emit('remove');
  }

  notifyChange(): void {
    this.$emit('change');
  }

  getCellName(cellId: number): string {
    return this.cells.find(e => e.id === cellId)?.name ?? this.$t('todo.scanCell.unknownCell').toString();
  }
};

